/* eslint-disable new-cap */
import { getProfile } from '@api/form-verification';
import { getHash } from '@api/tawkto';
import { tawkToConfig } from '@config/addn-configs';
import { FORM_URL_KEY } from '@global/constants/form';
import { getAccessToken } from '@global/helpers/general';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel';
import { RootState } from '@redux/store';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const AI_NAME = 'AI Assistant';

// Lazy load TawkMessengerReact only when needed
const TawkMessengerReact = React.lazy(() => import('@tawk.to/tawk-messenger-react'));

export default function TawkTo() {
  const tawkToRef = useRef<any>(null);
  const [shouldLoadTawk, setShouldLoadTawk] = useState(false);
  const location = useLocation();
  const tawkTo = import.meta.env.VITE_TEST ? null : tawkToConfig();
  const { searchedQuery, currentVerification } = useSelector((s: RootState) => s.misc);
  const [hash, setHash] = useState('');
  const formData = useSelector((s: RootState) => s.formData);
  const { showInitScreen } = formData ?? {};

  // Delay Tawk.to loading to improve initial performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoadTawk(true);
    }, 3000); // Load after 3 seconds

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (tawkTo && import.meta.env.VITE_ORG !== 'TEST' && shouldLoadTawk) {
      let intervalToFindTawkto: any = null;
      const loc = window.location;

      if (
        (loc.pathname.includes(`/${FORM_URL_KEY}`) || loc?.pathname === '/verification') &&
        !showInitScreen
      ) {
        intervalToFindTawkto = setInterval(() => {
          if (window.Tawk_API && window.Tawk_API.showWidget) {
            window.Tawk_API.showWidget();
          }
          const d = document.getElementsByClassName('widget-visible')?.[0];
          if (d) {
            d.setAttribute('style', 'display: block !important;');
            clearInterval(intervalToFindTawkto);
          }
        }, 500);
      } else {
        intervalToFindTawkto = setInterval(() => {
          if (tawkToRef.current) {
            tawkToRef.current.autoStart = false;
          }
          if (window.Tawk_API.hideWidget) {
            window.Tawk_API.hideWidget();
          }
          const d = document.getElementsByClassName('widget-visible')?.[0];
          if (d) {
            d.setAttribute('style', 'display: none !important;');
            clearInterval(intervalToFindTawkto);
          }
        }, 500);
      }

      return () => clearInterval(intervalToFindTawkto);
    }
  }, [location.pathname, showInitScreen, shouldLoadTawk]);

  // closure for sending fresh attributes whenever they change, gets triggered everytime the user sends message
  function onChatMessageVisitor() {
    let location = '';
    let firstMessageSent = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function trigger(hash: any) {
      try {
        if (location !== window.location.href) {
          location = window.location.href;
          window?.Tawk_API?.addEvent(
            'users-location', // Event name
            { location }, // Metadata (optional)
            function (error: any) {
              if (error) {
              } else {
              }
            }
          );
        }

        if (!firstMessageSent) {
          mixpanel.track(trackingEvents.tawkTo.events.chatStart, {
            location: window.location.href,
            orgId: searchedQuery?.orgId,
            txnId: searchedQuery?.txnId
          });
          firstMessageSent = true;
        }
      } catch {}
    }
    return trigger;
  }

  // closure for sending fresh attributes whenever they change, gets triggered everytime the user sends message
  function onChatMessageAgent() {
    let firstMessageSent = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function trigger(data: any) {
      try {
        if (!firstMessageSent) {
          mixpanel.track(trackingEvents.tawkTo.events.chatReplied, {
            location: window.location.href,
            orgId: searchedQuery?.orgId,
            txnId: searchedQuery?.txnId
          });
          firstMessageSent = true;
        }
      } catch {}
    }
    return trigger;
  }

  const triggerOnChatMessageVisitor = onChatMessageVisitor();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const triggerOnChatMessageAgent = onChatMessageAgent();

  const setUpAttributes = async () => {
    const token = getAccessToken();
    if (token) {
      const wallet = await getProfile();
      if (wallet?.code === 0) {
        const tempHash = await getHash(wallet?.data?.mobile);
        setHash(tempHash);
        window.tawkToReactElement = tawkToRef.current;
        try {
          const email: string | null =
            import.meta.env.VITE_ORG == 'ECE'
              ? 'ece@trential.com'
              : import.meta.env.VITE_ORG == 'GCE'
                ? 'gce@trential.com'
                : null;
          tawkToRef?.current?.setAttributes(
            {
              phone: wallet?.data?.mobile,
              name: `${wallet?.data?.mobile} , ${wallet?.data?.profileDetails?.name ?? '-'}`,
              ...(email ? { email } : {}),
              hash: tempHash
            },
            function () {
              // do something if error
            }
          );
        } catch {}
      }
    }
  };

  return tawkTo && shouldLoadTawk ? (
    <Suspense fallback={null}>
      <TawkMessengerReact
        propertyId={tawkTo?.propertyId}
        widgetId={tawkTo?.widgetId}
        ref={tawkToRef}
        onLoad={async () => {
          await setUpAttributes();
        }}
        onChatStarted={async () => {
          mixpanel.track(trackingEvents.tawkTo.events.widgetStarted, {
            name: currentVerification,
            location: window.location.href,
            orgId: searchedQuery?.orgId,
            txnId: searchedQuery?.txnId
          });

          await setUpAttributes();
        }}
        onChatMessageVisitor={() => {
          triggerOnChatMessageVisitor(hash);
        }}
        onAgentJoinChat={(data: any) => {
          let eventToEmit: keyof typeof trackingEvents.tawkTo.events | null = null;
          if (data?.position === AI_NAME) {
            eventToEmit = 'chatJoinedByAI';
          } else if (data?.name && data?.name !== 'Customer Support') {
            eventToEmit = 'chatJoinedBySupport';
          } else {
            return null;
          }
          mixpanel.track(trackingEvents.tawkTo.events[eventToEmit] as any, {
            name: currentVerification,
            location: window.location.href,
            orgId: searchedQuery?.orgId,
            txnId: searchedQuery?.txnId
          });
        }}
      />
    </Suspense>
  ) : null;
}
